import { head, isNil } from 'lodash/fp';
import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../../global';
import { buyOrderPeopleSelectors } from '../../components/admin/buy-process/people/people-redux';
import { CUSTOMER_FORM_ID } from '../../global/constants';
import { mapRelationsToPeople } from '../../components/admin/buy-process/buy-order-edit/utils/map-relations-to-people';
import { PersonRelation } from '../../components/admin/buy-process/buy-order-edit/buy-order-edit-types';
import { buyOrderOrganizationsSelectors } from '../../components/admin/buy-process/organizations/organizations-redux';

/*
 * Function to omit the entity IDs when the order is a new order.
 * */
function omitEntityIdentifiers<T extends Record<string, unknown>>(
  orderId: string,
  entity: T,
) {
  if (orderId === CUSTOMER_FORM_ID) {
    const entityCopy = { ...entity };

    delete entityCopy.id;
    delete entityCopy.personId;
    delete entityCopy.relationId;
    delete entityCopy.signerId;
    delete entityCopy.organizationId;

    return entityCopy;
  }

  return entity;
}

export const selectBuyPhysicalPeopleFactory = (orderId: string) =>
  createSelector(
    (state: RootState) => state.buyOrderEdit[orderId],
    (state: RootState) => state.buyOrderPeople[orderId],
    (buyOrder, peopleEntities) =>
      mapRelationsToPeople(
        orderId,
        buyOrder.ownerShares.physicalPeople,
        buyOrderPeopleSelectors.selectAll(peopleEntities),
      ),
  );

export const selectBuyLegalShareholdersFactory = (orderId: string) =>
  createSelector(
    (state: RootState) => state.buyOrderEdit[orderId],
    (state: RootState) => state.buyOrderPeople[orderId],
    (state: RootState) => state.buyOrderOrganizations[orderId],
    (buyOrder, peopleEntities, organizationEntities) => {
      const { legalShareholders } = buyOrder.ownerShares;

      return legalShareholders.map((legalShareholder) => {
        const organization = buyOrderOrganizationsSelectors.selectById(
          organizationEntities,
          legalShareholder.organization,
        );

        if (!organization) {
          throw new Error(
            `Legal shareholder points to a non-existent
            organization entity with entity id: ${legalShareholder.organization}`,
          );
        }

        // A legal-person shareholder can have up to four authorized representatives (signers).
        const signers = organization.signers.map((orgSigner) => {
          const person = buyOrderPeopleSelectors.selectById(
            peopleEntities,
            orgSigner.socialSecurityNumber,
          );

          if (!person) {
            throw new Error(
              `Organization entity with number ${organization.organizationNumber} points 
              to a non-existent person entity with social security number: ${orgSigner.socialSecurityNumber}`,
            );
          }

          return {
            personId: person.personId,
            id: orgSigner.signerId,
            person: {
              firstName: person.firstName,
              lastName: person.lastName,
              socialSecurityNumber: person.socialSecurityNumber,
            },
          };
        });

        return {
          organizationAddress: organization.organizationAddress,
          organizationCity: organization.organizationCity,
          organizationId: organization.organizationId,
          organizationName: organization.organizationName,
          organizationNumber: organization.organizationNumber,
          organizationPostalCode: organization.organizationPostalCode,

          numberShares: legalShareholder.numberShares,
          relationType: legalShareholder.relationType,

          signers,
        };
      });
    },
  );

export const selectBuyRepresentativesPeopleFactory = (orderId: string) =>
  createSelector(
    (state: RootState) => state.buyOrderEdit[orderId],
    (state: RootState) => state.buyOrderPeople[orderId],
    (buyOrder, peopleEntities) =>
      mapRelationsToPeople(
        orderId,
        buyOrder.representatives.representatives,
        buyOrderPeopleSelectors.selectAll(peopleEntities),
      ),
  );

export const selectBuyCeoPersonFactory = (orderId: string) =>
  createSelector(
    (state: RootState) => state.buyOrderEdit[orderId],
    (state: RootState) => state.buyOrderPeople[orderId],
    (buyOrder, peopleEntities) => {
      function hasCeo(ceo: PersonRelation | undefined): ceo is PersonRelation {
        return buyOrder.representatives.officialCeo;
      }

      if (!hasCeo(buyOrder.representatives.ceo)) return null;

      return head(
        mapRelationsToPeople(
          orderId,
          [buyOrder.representatives.ceo],
          buyOrderPeopleSelectors.selectAll(peopleEntities),
        ),
      );
    },
  );

export const selectBuyAuditorPersonFactory = (orderId: string) =>
  createSelector(
    (state: RootState) => state.buyOrderEdit[orderId],
    (state: RootState) => state.buyOrderPeople[orderId],
    (buyOrder, peopleEntities) => {
      const entityIdentifier = buyOrder.auditor.auditorPerson?.person;

      if (!entityIdentifier || isNil(buyOrder.auditor.auditorPerson)) return undefined;

      const auditorPersonEntity = buyOrderPeopleSelectors.selectById(
        peopleEntities,
        entityIdentifier,
      );

      if (!auditorPersonEntity) {
        return undefined;
      }

      return omitEntityIdentifiers(orderId, auditorPersonEntity);
    },
  );

export const selectBuyPowerOfAttorneyPersonFactory = (orderId: string) =>
  createSelector(
    (state: RootState) => state.buyOrderEdit[orderId],
    (state: RootState) => state.buyOrderPeople[orderId],
    (buyOrder, peopleEntities) => {
      const entityIdentifier = buyOrder.proxies.person;

      const powerOfAttorneyPersonEntity = buyOrderPeopleSelectors.selectById(
        peopleEntities,
        entityIdentifier,
      );

      return omitEntityIdentifiers(orderId, {
        ...powerOfAttorneyPersonEntity,
        relationId: buyOrder.proxies.relationId,
      });
    },
  );

export const selectBuyClientInfoPersonFactory = (orderId: string) =>
  createSelector(
    (state: RootState) => state.buyOrderEdit[orderId],
    (state: RootState) => state.buyOrderPeople[orderId],
    (buyOrder, peopleEntities) => {
      const { person: clientInfoEntityId, ...clientInfo } = buyOrder.clientInfo;

      if (!clientInfoEntityId) {
        return omitEntityIdentifiers(orderId, {
          ...clientInfo,
          firstName: buyOrder.clientInfo.firstName,
          lastName: buyOrder.clientInfo.lastName,
          address: buyOrder.clientInfo.address,
          city: buyOrder.clientInfo.city,
          postalCode: buyOrder.clientInfo.postalCode,
        });
      }

      const clientInfoPersonEntity = buyOrderPeopleSelectors.selectById(
        peopleEntities,
        clientInfoEntityId,
      );

      return omitEntityIdentifiers(orderId, {
        ...clientInfo,
        ...clientInfoPersonEntity,
      });
    },
  );

export const selectBuyDeleteRelationsFactory = (orderId: string) =>
  createSelector(
    (state: RootState) => state.buyOrderEdit[orderId].deleteRequest,
    (deleteRequest) => deleteRequest,
  );
